import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { sendContactMessage } from "@/lib/contact.functions";
import { useT } from "@/lib/i18n";

export function DefaultContactForm() {
  const { tk } = useT();
  const sendMessage = useServerFn(sendContactMessage);

  const [details, setDetails] = useState({ fullName: "", email: "", phone: "", reason: "" });
  const [accepted, setAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const contactMutation = useMutation({
    mutationFn: () =>
      sendMessage({
        data: {
          fullName: details.fullName,
          email: details.email,
          phone: details.phone,
          message: details.reason,
          marketingConsent,
        },
      }),
    onSuccess: () => {
      toast.success(tk("contacto.6eb63c", "Mensaje enviado. Le contactaremos en breve."));
      setDetails({ fullName: "", email: "", phone: "", reason: "" });
      setAccepted(false);
      setMarketingConsent(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="max-w-2xl mx-auto px-6 py-16">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!accepted) return toast.error(tk("contacto.0468b3", "Acepte la política de privacidad"));
          if (details.reason.trim().length < 10)
            return toast.error(tk("contacto.3fad3f", "Describa brevemente su consulta (mín. 10 caracteres)"));
          contactMutation.mutate();
        }}
        className="rounded-2xl border border-gold/20 bg-card/30 p-8 md:p-12 space-y-6"
      >
        {[
          { n: "fullName", l: tk("contacto.c3a7af", "Nombre y apellidos"), t: "text" },
          { n: "email", l: "Email", t: "email" },
          { n: "phone", l: tk("contacto.624704", "Teléfono móvil"), t: "tel" },
        ].map((f) => (
          <div key={f.n}>
            <label className="text-[10px] tracking-[0.3em] uppercase text-foreground/75">{f.l} *</label>
            <input
              type={f.t}
              required
              value={(details as Record<string, string>)[f.n]}
              onChange={(e) => setDetails({ ...details, [f.n]: e.target.value })}
              className="mt-2 w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2"
            />
          </div>
        ))}
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-foreground/75">
            {tk("contacto.mensaje.label", "Cuéntanos tu reserva o consulta (fecha, nº de personas, actividades)")} *
          </label>
          <textarea
            required
            rows={6}
            value={details.reason}
            onChange={(e) => setDetails({ ...details, reason: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2 resize-none"
          />
        </div>
        <label className="flex items-start gap-3 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
            className="mt-1 accent-[var(--gold)]"
          />
          <span>{tk("contacto.marketing.es", "Quiero recibir noticias, promociones y novedades del Bosque Suspendido.")}</span>
        </label>
        <label className="flex items-start gap-3 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 accent-[var(--gold)]"
          />
          <span>
            {tk("contacto.6fad57.pre", "He leído y acepto la ")}
            <a
              href="/privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold underline hover:text-[oklch(0.88_0.08_85)]"
            >
              {tk("contacto.6fad57.link", "política de privacidad")}
            </a>
            .
          </span>
        </label>
        <button
          type="submit"
          disabled={contactMutation.isPending}
          className="w-full wood-btn py-4 text-xs tracking-[0.3em] uppercase disabled:opacity-60"
        >
          {contactMutation.isPending
            ? tk("contacto.5e91dc", "Enviando…")
            : tk("contacto.b25cab", "Enviar mensaje")}
        </button>
      </form>
    </section>
  );
}
